// The Delivery home's rules, free of React so they test (model.test.ts):
// month totals and pacing, the progress chart's series and holiday pins, the
// columns per project and team, months ahead, and the holiday list. Business
// days come from the server's calendar (ops.business_day); nothing here
// decides what a holiday is.
import type { AheadRow, CapacityMonthRow, PacingDay, PacingMonth, PacingTeamRow, PacingUnit } from './pacingApi';

export const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DOW3 = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const utc = (iso: string) => new Date(`${iso}T00:00:00Z`);
const isoOf = (d: Date) => d.toISOString().slice(0, 10);
export const addDays = (iso: string, n: number) => {
  const d = utc(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return isoOf(d);
};
const dow = (iso: string) => utc(iso).getUTCDay();
const isWeekend = (iso: string) => dow(iso) === 0 || dow(iso) === 6;

/** "Oct 5". */
export const shortDate = (iso: string) => `${SHORT_MONTHS[utc(iso).getUTCMonth()]} ${utc(iso).getUTCDate()}`;
/** "Mon Oct 5". */
export const dayDate = (iso: string) => `${DOW3[dow(iso)]} ${shortDate(iso)}`;
/** "Wednesday, October 21". */
export const longDate = (iso: string) => `${DOW[dow(iso)]}, ${FULL_MONTHS[utc(iso).getUTCMonth()]} ${utc(iso).getUTCDate()}`;
/** "October 2026". */
export const monthLabel = (month: string) => `${FULL_MONTHS[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
/** "Nov", or "Jan ’27" in January. */
export const monthShort = (month: string) => `${SHORT_MONTHS[Number(month.slice(5, 7)) - 1]}${month.slice(5, 7) === '01' ? ` ’${month.slice(2, 4)}` : ''}`;
export const addMonths = (month: string, n: number) => {
  const i = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + n;
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`;
};

/** Hours to a tenth, no trailing zero: "12", "12.5". */
export function hours(n: number): string {
  const r = Math.round(n * 10) / 10;
  return Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1);
}
/** A whole-number percentage: "64%". */
export const pct = (n: number) => `${Math.round(n)}%`;
/** A whole-number signed percentage: "+3%", "−18%". */
export function pts(n: number): string {
  const r = Math.round(n);
  return `${r >= 0 ? '+' : '−'}${Math.abs(r)}%`;
}
export const tone = (n: number): 'positive' | 'negative' => (n >= 0 ? 'positive' : 'negative');

// ── Units: my projects and deals ──────────────────────────────────────────

export interface Unit extends PacingUnit {
  /** What the chart prints under the column: the project's code, unique on the page. */
  label: string;
}

function initials(name: string): string {
  const letters = name.split(/[^A-Za-z0-9]+/).filter(Boolean).map((w) => w[0]!.toUpperCase()).join('');
  return letters.slice(0, 5) || 'DEAL';
}

/**
 * Gives every unit a short label. A project's code is unique, so it is the
 * label. A deal borrows its account's project code, or its initials, with ·2,
 * ·3 when that is taken, so a deal beside its account's project reads ACME·2.
 */
export function labelUnits<T extends Pick<PacingUnit, 'key' | 'code' | 'name'> & { is_deal?: boolean }>(units: readonly T[]): (T & { label: string })[] {
  const projectCode = (u: T) => (u.is_deal === true ? null : u.code);
  const seen = new Map<string, number>();
  for (const u of units) {
    const code = projectCode(u);
    if (code !== null) seen.set(code, 1);
  }
  const labels = new Map<string, string>();
  for (const u of units) {
    const code = projectCode(u);
    if (code !== null) {
      labels.set(u.key, code);
      continue;
    }
    const base = u.code ?? initials(u.name);
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    labels.set(u.key, n === 1 ? base : `${base}·${n}`);
  }
  return units.map((u) => ({ ...u, label: labels.get(u.key)! }));
}

// ── The month ─────────────────────────────────────────────────────────────

export interface MonthPace {
  total: number;
  elapsed: number;
  remaining: number;
  /** elapsed ÷ total, as a percentage. */
  pct: number;
}

/** Business days of a month as of today: elapsed are those strictly before today. */
export function monthPace(days: readonly PacingDay[], today: string): MonthPace {
  const business = days.filter((d) => d.business);
  const elapsed = business.filter((d) => d.date < today).length;
  return { total: business.length, elapsed, remaining: business.length - elapsed, pct: business.length ? (elapsed / business.length) * 100 : 0 };
}

export interface Totals {
  forecast: number;
  confirmed: number;
  assigned: number;
  billable: number;
  nonBillable: number;
  attainment: number;
  /** Attainment minus the share of the month elapsed. */
  pacing: number;
  neededPerDay: number;
}

export function totals(units: readonly PacingUnit[], pace: MonthPace): Totals {
  const sum = (f: (u: PacingUnit) => number) => units.reduce((a, u) => a + f(u), 0);
  const forecast = sum((u) => u.forecast_hours);
  const billable = sum((u) => u.billable_hours);
  const attainment = forecast ? (billable / forecast) * 100 : 0;
  return {
    forecast,
    confirmed: sum((u) => u.confirmed_hours),
    assigned: sum((u) => u.assigned_hours),
    billable,
    nonBillable: sum((u) => u.logged_hours - u.billable_hours),
    attainment,
    pacing: attainment - pace.pct,
    neededPerDay: Math.max(forecast - billable, 0) / Math.max(pace.remaining, 1),
  };
}

// ── Monthly progress ──────────────────────────────────────────────────────

export type MonthState = 'past' | 'current' | 'future';

export interface ProgressSeries {
  state: MonthState;
  days: { key: string; label: string; title: string; tick: boolean }[];
  values: number[];
  forecast: number;
  /** Finished business days, for the projection (current month only). */
  elapsed: number | undefined;
  today: number | undefined;
  milestones: { before: number; label: string }[];
  total: number;
  /** Average per finished day (current) or per day (past). */
  rate: number;
}

/**
 * The chart for one month and one project (or all): billable hours per
 * business day, with time logged on a weekend or holiday counted on the
 * business day before it (or the first one, before the month's first).
 */
export function progressSeries(view: PacingMonth, today: string, unitKey: string | null): ProgressSeries {
  const current = today.slice(0, 7);
  const state: MonthState = view.month < current ? 'past' : view.month === current ? 'current' : 'future';
  const business = view.days.filter((d) => d.business).map((d) => d.date);
  const n = business.length;
  const units = unitKey ? view.units.filter((u) => u.key === unitKey) : view.units;
  const forecast = units.reduce((a, u) => a + u.forecast_hours, 0);
  const byDay = new Array<number>(n).fill(0);
  for (const row of view.daily) {
    if (unitKey && row.key !== unitKey) continue;
    let i = business.findIndex((d) => d > row.date) - 1;
    if (i === -2) i = n - 1;
    if (i < 0) i = 0;
    if (n) byDay[i] = (byDay[i] ?? 0) + row.hours;
  }
  const elapsed = state === 'past' ? n : state === 'current' ? business.filter((d) => d < today).length : 0;
  const todayIndex = state === 'current' ? business.indexOf(today) : -1;
  // Today shows once it has hours; the projection starts from the finished days.
  const shown = state === 'current' ? elapsed + (todayIndex >= 0 && (byDay[todayIndex] ?? 0) > 0 ? 1 : 0) : elapsed;
  const values = byDay.slice(0, shown);
  const total = values.reduce((a, b) => a + b, 0);
  const atElapsed = byDay.slice(0, elapsed).reduce((a, b) => a + b, 0);
  return {
    state,
    days: business.map((d, i) => ({ key: d, label: shortDate(d), title: dayDate(d), tick: i === 0 || dow(d) === 1 })),
    values,
    forecast,
    elapsed: state === 'current' ? elapsed : undefined,
    today: todayIndex >= 0 ? todayIndex : undefined,
    milestones: holidayMilestones(view.days),
    total,
    rate: elapsed ? atElapsed / elapsed : 0,
  };
}

/** Holidays as pins between business days: days off in a row are one pin ("Thanksgiving · 2 days"). */
export function holidayMilestones(days: readonly PacingDay[]): { before: number; label: string }[] {
  const out: { before: number; name: string; days: number }[] = [];
  let before = 0;
  for (const d of days) {
    if (d.business) {
      before += 1;
      continue;
    }
    if (!d.holiday) continue;
    const last = out[out.length - 1];
    if (last && last.before === before) last.days += 1;
    else out.push({ before, name: d.holiday, days: 1 });
  }
  return out.map((m) => ({ before: m.before, label: m.days > 1 || /break/i.test(m.name) ? `${m.name} · ${m.days} ${m.days === 1 ? 'day' : 'days'}` : m.name }));
}

export interface Callout {
  label: string;
  value: string;
  tone?: 'positive' | 'negative';
}

export function progressCallouts(s: ProgressSeries): Callout[] {
  const n = s.days.length;
  if (s.state === 'current') {
    const finished = s.elapsed ?? 0;
    const atElapsed = s.rate * finished;
    const gap = atElapsed - (s.forecast * finished) / Math.max(n, 1);
    return [
      { label: 'Versus pace', value: `${gap >= 0 ? '+' : '−'}${hours(Math.abs(gap))} h`, tone: tone(gap) },
      { label: 'Average per day', value: `${hours(s.rate)} h` },
      { label: 'Needed per day', value: `${hours(Math.max(s.forecast - s.total, 0) / Math.max(n - finished, 1))} h` },
    ];
  }
  if (s.state === 'past') {
    if (!s.forecast) {
      return [
        { label: 'Billable', value: `${hours(s.total)} h` },
        { label: 'Attainment', value: '—' },
        { label: 'Average per day', value: `${hours(s.rate)} h` },
      ];
    }
    const a = (s.total / s.forecast) * 100;
    return [
      { label: 'Billable', value: `${hours(s.total)} of ${hours(s.forecast)} h` },
      { label: 'Attainment', value: pct(a), tone: a >= 100 ? 'positive' : 'negative' },
      { label: 'Average per day', value: `${hours(s.rate)} h` },
    ];
  }
  return [
    { label: 'Forecast', value: `${hours(s.forecast)} h` },
    { label: 'Business days', value: String(n) },
    { label: 'Needed per day', value: `${hours(s.forecast / Math.max(n, 1))} h` },
  ];
}

// ── Columns ───────────────────────────────────────────────────────────────

export interface UnitPace {
  unit: Unit;
  forecast: number;
  billable: number;
  /** Where it should be today. */
  onPace: number;
  attainment: number | null;
  pacing: number | null;
  neededPerDay: number;
}

export function unitPace(unit: Unit, forecast: number, billable: number, pace: MonthPace): UnitPace {
  const attainment = forecast ? (billable / forecast) * 100 : null;
  return {
    unit,
    forecast,
    billable,
    onPace: pace.total ? (forecast * pace.elapsed) / pace.total : 0,
    attainment,
    pacing: attainment === null ? null : attainment - pace.pct,
    neededPerDay: Math.max(forecast - billable, 0) / Math.max(pace.remaining, 1),
  };
}

export interface TeamPace extends UnitPace {
  mine: number;
  people: (PacingTeamRow & { you: boolean; attainment: number | null; pacing: number | null })[];
}

/** Each of my projects with its whole team this month. */
export function teamPace(units: readonly Unit[], team: readonly PacingTeamRow[], meId: string, pace: MonthPace): TeamPace[] {
  return units.map((unit) => {
    const rows = team.filter((t) => t.key === unit.key);
    const people = (rows.length ? rows : [{
      key: unit.key, app_user_id: meId, name: 'You', has_photo: false, forecast_hours: unit.forecast_hours, billable_hours: unit.billable_hours,
    }]).map((p) => {
      const a = p.forecast_hours ? (p.billable_hours / p.forecast_hours) * 100 : null;
      return { ...p, you: p.app_user_id === meId, attainment: a, pacing: a === null ? null : a - pace.pct };
    });
    const forecast = people.reduce((s, p) => s + p.forecast_hours, 0);
    const billable = people.reduce((s, p) => s + p.billable_hours, 0);
    return { ...unitPace(unit, forecast, billable, pace), mine: unit.billable_hours, people };
  });
}

// ── Months ahead ──────────────────────────────────────────────────────────

export interface AheadMonth {
  month: string;
  businessDays: number;
  capacity: number;
  assigned: number;
  utilization: number | null;
  byUnit: Map<string, number>;
}

export const capacityOf = (m: CapacityMonthRow) => m.capacity;

/** The Assignments tab: months after this one, what I'm assigned on each project against my capacity. */
export function aheadMonths(months: readonly CapacityMonthRow[], ahead: readonly AheadRow[], first: string, count: number): AheadMonth[] {
  const keys = Array.from({ length: count }, (_, i) => addMonths(first, i));
  return keys.map((month) => {
    const cap = months.find((m) => m.month === month);
    const byUnit = new Map<string, number>();
    for (const r of ahead) if (r.month === month) byUnit.set(r.key, (byUnit.get(r.key) ?? 0) + r.hours);
    const assigned = [...byUnit.values()].reduce((a, b) => a + b, 0);
    const capacity = cap ? capacityOf(cap) : 0;
    return { month, businessDays: cap?.business_days ?? 0, capacity, assigned, utilization: capacity ? (assigned / capacity) * 100 : null, byUnit };
  });
}

/** The units the Assignments table lists, in first-seen order, labelled. */
export function aheadUnits(ahead: readonly AheadRow[]) {
  const seen = new Map<string, AheadRow>();
  for (const r of ahead) if (!seen.has(r.key)) seen.set(r.key, r);
  return labelUnits([...seen.values()]);
}

// ── Holidays ──────────────────────────────────────────────────────────────

export interface HolidayItem {
  start: string;
  end: string;
  name: string;
  days: number;
  /** The first business day after it. */
  back: string;
  /** Business days from today until it starts. */
  away: number;
}

/** Holidays from today, days off in a row with one name grouped ("Dec 24 – Jan 1 · back Mon Jan 4"). */
export function holidayList(holidays: readonly { date: string; name: string }[], today: string): HolidayItem[] {
  const off = new Set(holidays.map((h) => h.date));
  const isBusiness = (d: string) => !isWeekend(d) && !off.has(d);
  const nextBusiness = (d: string) => {
    let x = addDays(d, 1);
    for (let i = 0; i < 40 && !isBusiness(x); i += 1) x = addDays(x, 1);
    return x;
  };
  const businessBetween = (a: string, b: string) => {
    let n = 0;
    for (let x = a; x < b; x = addDays(x, 1)) if (isBusiness(x)) n += 1;
    return n;
  };
  const items: HolidayItem[] = [];
  for (const h of [...holidays].sort((a, b) => a.date.localeCompare(b.date))) {
    if (h.date < today) continue;
    const last = items[items.length - 1];
    if (last && last.name === h.name && businessBetween(addDays(last.end, 1), h.date) === 0) {
      last.end = h.date;
      last.days += 1;
    } else {
      items.push({ start: h.date, end: h.date, name: h.name, days: 1, back: '', away: businessBetween(today, h.date) });
    }
  }
  for (const it of items) it.back = nextBusiness(it.end);
  return items;
}
